import { AppModal } from '../../../../components/AppModal';
import { message, Tooltip } from 'antd';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

import styles from './index.module.scss';
import { EndUserButton } from '../../../../components/EndUserButton';
import { useContainerQuery } from '../../../../lib/hooks/endUserHooks/useContainerQuery';
import { openLinkAndTrack } from '../../../../lib/utils/sendDataToAnalytics';
import { useAppSelector } from '../../../../lib/store/hooks';
import { useUpdateCampaignFindWordMutation } from '../../../../lib/store/api/endUser';
import { useIsEnergieScwabenTenant } from '../../../../lib/hooks/endUserHooks/useIsEnergieScwabenTenant';

const ENERGIE_SCWABEN_SENTANCE = 'GEMEINSAM FÜR UNSERE REGION';
const TAUBER_SENTANCE = 'ECHTE BERATUNG & GUTE PREISE';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

type CharConfig =
  | { type: 'letter'; char: string; index: number }
  | { type: 'space'; index: number }
  | { type: 'fixed'; char: string; index: number };

const ALLOWED_CHARS_REGEX = /[A-ZÄÖÜ]/i;

export const CampaignEventModal: FC<Props> = ({ isOpen, onClose }) => {
  const { isEnergieSchwaben } = useIsEnergieScwabenTenant();

  const brand = isEnergieSchwaben ? 'energieschwaben' : 'tauber';
  const sentence = isEnergieSchwaben ? ENERGIE_SCWABEN_SENTANCE : TAUBER_SENTANCE;

  const { isMobile, isTablet } = useContainerQuery();
  const { activeCampaign, tenantAndProject } = useAppSelector(({ endUser }) => endUser);

  const normalizedSentence = useMemo(() => sentence.toUpperCase(), [sentence]);

  const charConfigs: CharConfig[] = useMemo(
    () =>
      normalizedSentence.split('').map((ch, index) => {
        if (ch === ' ') {
          return { type: 'space', index };
        }

        if (ch === '&') {
          return { type: 'fixed', char: ch, index };
        }

        if (ALLOWED_CHARS_REGEX.test(ch)) {
          return { type: 'letter', char: ch, index };
        }

        return { type: 'fixed', char: ch, index };
      }),
    [normalizedSentence],
  );

  const [submitted, setSubmitted] = useState(false);
  const [values, setValues] = useState<string[]>(() =>
    charConfigs.map((cfg) =>
      cfg.type === 'letter' ? '' : cfg.type === 'fixed' ? cfg.char : '',
    ),
  );

  const [messageApi, contextHolder] = message.useMessage();

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [findWord, { isLoading }] = useUpdateCampaignFindWordMutation();

  useEffect(() => {
    setValues(
      charConfigs.map((cfg) =>
        cfg.type === 'letter' ? '' : cfg.type === 'fixed' ? cfg.char : '',
      ),
    );
    setSubmitted(false);
  }, [charConfigs]);

  useEffect(() => {
    if (!activeCampaign?.isUserCompleteWord) return;

    setSubmitted(true);

    setValues(
      charConfigs.map((cfg) => {
        if (cfg.type === 'letter' || cfg.type === 'fixed') {
          return cfg.char;
        }
        return '';
      }),
    );
  }, [activeCampaign?.isUserCompleteWord, charConfigs]);

  const letterIndexes = useMemo(
    () =>
      charConfigs
        .map((cfg, idx) => (cfg.type === 'letter' ? idx : -1))
        .filter((idx) => idx !== -1),
    [charConfigs],
  );

  const focusNext = (currentIndex: number) => {
    const pos = letterIndexes.indexOf(currentIndex);
    if (pos === -1 || pos === letterIndexes.length - 1) return;
    const nextIndex = letterIndexes[pos + 1];
    inputRefs.current[nextIndex]?.focus();
  };

  const focusPrev = (currentIndex: number) => {
    const pos = letterIndexes.indexOf(currentIndex);
    if (pos <= 0) return;
    const prevIndex = letterIndexes[pos - 1];
    inputRefs.current[prevIndex]?.focus();
  };

  const handleChange = (index: number, raw: string) => {
    if (submitted) return;

    let value = raw.toUpperCase();

    value = value
      .split('')
      .filter((c) => ALLOWED_CHARS_REGEX.test(c))
      .join('');

    if (value.length > 1) {
      value = value[value.length - 1];
    }

    setValues((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });

    if (value) {
      focusNext(index);
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    const target = e.target as HTMLInputElement;
    const index = Number(target.dataset.index);

    if (e.key === 'Backspace' && !target.value) {
      e.preventDefault();
      focusPrev(index);
      setValues((prev) => {
        const next = [...prev];
        next[index] = '';
        return next;
      });
    }
  };

  const allFilled = useMemo(
    () => charConfigs.every((cfg, idx) => cfg.type !== 'letter' || Boolean(values[idx])),
    [charConfigs, values],
  );

  const isCorrect = useMemo(
    () =>
      charConfigs.every((cfg, idx) => {
        if (cfg.type === 'space') return true;
        if (cfg.type === 'fixed') return true;
        return values[idx]?.toUpperCase() === cfg.char;
      }),
    [charConfigs, values],
  );

  const canSubmit = !submitted && allFilled && isCorrect;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      await findWord({
        campaignId: Number(activeCampaign?.id),
        tenantId: String(tenantAndProject?.tenantId),
      }).unwrap();

      messageApi.success('Vielen Dank!');

      if (brand === 'energieschwaben') {
        setTimeout(() => {
          openLinkAndTrack(
            'https://www.energie-schwaben.de/gewinnspiele/gasgrill-roesle-2025',
          );
        }, 1000);
      }
    } catch (error) {
      messageApi.error('Something went wrong!');
    }
  };

  const wordIndices = useMemo(() => {
    const words: number[][] = [];
    let current: number[] = [];

    charConfigs.forEach((cfg, idx) => {
      if (cfg.type === 'space') {
        if (current.length) {
          words.push(current);
          current = [];
        }
        return;
      }
      current.push(idx);
    });

    if (current.length) {
      words.push(current);
    }

    return words;
  }, [charConfigs]);

  const tauberRows = useMemo(() => {
    if (brand !== 'tauber') return null;

    const w = wordIndices;

    if (w.length < 5) {
      return [[w[0] || []], w.slice(1)];
    }

    const mobileTabletPattern = [
      [w[0]], // "ECHTE"
      [w[1]], // "BERATUNG"
      [w[2], w[3]], // & "&"
      [w[4]], // "GUTE" + "PREISE"
    ];

    const defaultPattern = [
      [w[0]], // "ECHTE"
      [w[1], w[2]], // "BERATUNG" & "&"
      [w[3], w[4]], // "GUTE" + "PREISE"
    ];

    return isMobile || isTablet ? mobileTabletPattern : defaultPattern;
  }, [brand, isMobile, isTablet, wordIndices]);

  const esRows = useMemo(() => {
    if (brand !== 'energieschwaben') return null;

    const w = wordIndices;

    const mobileTabletPattern = [
      [w[0]], // "GEMEINSAM"
      [w[1]], // "FÜR"
      [w[2]], // "UNSERE"
      [w[3]], // "REGION"
    ];

    const defaultPattern = [
      [w[0]], // "GEMEINSAM"
      [w[1], w[2]], // "FÜR" & "UNSERE"
      [w[3]], // "REGION"
    ];

    return isMobile || isTablet ? mobileTabletPattern : defaultPattern;
  }, [brand, isMobile, isTablet, wordIndices]);

  const rows = brand === 'tauber' ? tauberRows : esRows;

  const renderCell = (cfg: CharConfig, index: number) => {
    if (cfg.type === 'fixed') {
      return (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          data-index={index}
          className={styles.letterInput}
          maxLength={1}
          value={values[index] ?? cfg.char}
          readOnly
        />
      );
    }

    if (cfg.type === 'letter') {
      return (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          data-index={index}
          className={styles.letterInput}
          maxLength={1}
          value={values[index] ?? ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={handleKeyDown}
          readOnly={submitted}
        />
      );
    }

    return null;
  };

  return (
    <AppModal
      open={isOpen}
      onCancel={onClose}
      modalWidth={742}
      className={clsx(styles.modal, {
        [styles.tauber]: brand === 'tauber',
        [styles.energieschwaben]: brand === 'energieschwaben',
      })}
      footer={null}
    >
      {contextHolder}

      <div className={styles.header}>
        <h3 className={styles.title}>Das große Weihnachtsrätsel</h3>
        <p className={styles.subtitle}>
          Haben Sie das Rätsel gelöst?
          <br /> Geben Sie Ihren Lösungssatz ein und sichern Sie sich die Chance auf tolle
          <br />
          Weihnachtsgewinne!
        </p>
      </div>

      <div className={styles.tauberGrid}>
        {rows?.map((row, rowIdx) => (
          <div
            key={rowIdx}
            className={clsx(styles.tauberRow, {
              [styles.tauberRowCenter]: rowIdx === 0,
            })}
          >
            {row.map((word, wi) => (
              <div key={wi} className={styles.tauberWord}>
                {word.map((cellIndex) => renderCell(charConfigs[cellIndex], cellIndex))}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className={styles.actions}>
        <Tooltip
          placement="top"
          title={canSubmit ? null : () => 'Geben Sie den korrekten Lösungssatz ein'}
        >
          <span style={{ display: 'inline-block' }}>
            <EndUserButton
              type="primary"
              onClick={handleSubmit}
              dynamicBackgroundColor={brand === 'tauber' ? '#ec6608' : '#078b65'}
              additionalStyles={{
                backgroundColor: brand === 'tauber' ? '#ec6608' : '#078b65',
              }}
              disabled={!canSubmit}
              isLoading={isLoading}
            >
              Jetzt am Gewinnspiel teilnehmen
            </EndUserButton>
          </span>
        </Tooltip>
      </div>
    </AppModal>
  );
};
